import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Spinner } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { ContractArea, ForemanArea } from '../../../services/areasService';

interface ContractAreaModalProps {
  show: boolean;
  onHide: () => void;
  contractArea: ContractArea | null;
  foremanAreas: ForemanArea[];
  onSave: (contractAreaData: Partial<Omit<ContractArea, 'foremanAreas' | 'assets'>> & { foremanAreaIds?: number[] }) => Promise<void>;
}

const ContractAreaModal: React.FC<ContractAreaModalProps> = ({
  show,
  onHide,
  contractArea,
  foremanAreas,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    foremanAreaId: '', // Keep for backward compatibility
    selectedForemanAreas: [] as number[]
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (contractArea) {
      setFormData({
        name: contractArea.name || '',
        code: contractArea.code || '',
        foremanAreaId: '',
        selectedForemanAreas: contractArea.foremanAreas?.map(fa => fa.id) || []
      });
    } else {
      setFormData({
        name: '',
        code: '',
        foremanAreaId: '',
        selectedForemanAreas: []
      });
    }
    setError(null);
  }, [contractArea, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const dataToSend = {
        name: formData.name,
        code: formData.code,
        foremanAreaIds: formData.selectedForemanAreas.length > 0 
          ? formData.selectedForemanAreas 
          : (formData.foremanAreaId ? [parseInt(formData.foremanAreaId)] : [])
      };
      await onSave(dataToSend);
      onHide();
    } catch (err) {
      console.error('Error saving contract area:', err);
      setError('Failed to save contract area. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isFormValid = formData.name.trim().length > 0 && 
    (formData.selectedForemanAreas.length > 0 || formData.foremanAreaId !== '');

  // Group foreman areas by service area for better UX
  const foremanAreasByServiceArea = foremanAreas.reduce((groups, area) => {
    const serviceAreaName = area.serviceArea?.name || 'Unknown Service Area';
    if (!groups[serviceAreaName]) {
      groups[serviceAreaName] = [];
    }
    groups[serviceAreaName].push(area);
    return groups;
  }, {} as { [key: string]: ForemanArea[] });

  const modalFooter = (
    <>
      <Button variant="secondary" onClick={onHide} disabled={loading}>
        Cancel
      </Button>
      <Button 
        variant="primary" 
        type="submit" 
        disabled={!isFormValid || loading}
        onClick={handleSubmit}
      >
        {loading ? (
          <>
            <Spinner animation="border" size="sm" className="tw-mr-2" />
            Saving...
          </>
        ) : (
          <>
            <i className="bi bi-check-lg tw-mr-2"></i>
            {contractArea ? 'Update' : 'Create'} Contract Area
          </>
        )}
      </Button>
    </>
  );

  return (
    <ResponsiveModal
      isOpen={show}
      onClose={onHide}
      title={contractArea ? 'Edit Contract Area' : 'Create New Contract Area'}
      footer={modalFooter}
    >
      {error && (
        <Alert variant="danger">{error}</Alert>
      )}

      <Form.Group className="tw-mb-3">
        <Form.Label>Name *</Form.Label>
        <Form.Control
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="Enter contract area name"
          required
          disabled={loading}
        />
      </Form.Group>

      <Form.Group className="tw-mb-3">
        <Form.Label>Code</Form.Label>
        <Form.Control
          type="text"
          value={formData.code}
          onChange={(e) => handleInputChange('code', e.target.value)}
          placeholder="Enter contract area code (optional)"
          disabled={loading}
        />
        <Form.Text className="tw-text-content-muted">
          Optional identifier code for this contract area
        </Form.Text>
      </Form.Group>

      <p className="tw-text-content-muted tw-text-sm tw-mb-3">
        Public map markers and popup contact text are managed on each <strong>Service Area</strong> (foreman areas inherit that map block).
      </p>

      <Form.Group className="tw-mb-3">
        <Form.Label>Foreman Areas * (Select one or more)</Form.Label>
        <div className="tw-border tw-border-line tw-rounded tw-p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {Object.entries(foremanAreasByServiceArea).map(([serviceAreaName, areas]) => (
            <div key={serviceAreaName}>
              <h6 className="tw-text-primary tw-mb-2">{serviceAreaName}</h6>
              {areas.map(foremanArea => (
                <Form.Check
                  key={foremanArea.id}
                  type="checkbox"
                  id={`foreman-area-${foremanArea.id}`}
                  label={`${foremanArea.name} ${foremanArea.code ? `(${foremanArea.code})` : ''}`}
                  checked={formData.selectedForemanAreas.includes(foremanArea.id)}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    const updatedAreas = isChecked
                      ? [...formData.selectedForemanAreas, foremanArea.id]
                      : formData.selectedForemanAreas.filter(id => id !== foremanArea.id);
                    
                    setFormData(prev => ({
                      ...prev,
                      selectedForemanAreas: updatedAreas
                    }));
                  }}
                  disabled={loading}
                  className="tw-ml-3 tw-mb-1"
                />
              ))}
              <hr className="tw-my-2" />
            </div>
          ))}
        </div>
        <Form.Text className="tw-text-content-muted">
          Select one or more foreman areas this contract area belongs to. You can select areas from multiple service areas.
        </Form.Text>
      </Form.Group>
    </ResponsiveModal>
  );
};

export default ContractAreaModal;